import React, { useState, useEffect, useRef } from 'react';
import style from './StepsMain.module.css';
import HeadSub from '../../../../../components/comm/HeadSub';
import imgSteps from '../../../../../images/user/img_3d_steps_main.svg';
import { useNavigate } from 'react-router-dom';
import URL from '../../../../../constants/url';
import { healthAPI } from '../../../../../api/healthAPI';
import Modal from '../../../../../components/comm/Modal';
import { useWebStepCounter } from '../../../../../hook/useWebStepCounter';

const detectEnvironment = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isAndroid = /Android/i.test(userAgent) || navigator.platform === 'Linux armv7l' || navigator.platform === 'Linux aarch64' || userAgent.includes('wv');
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;

    return {
        isReactNativeWebView: !!window.ReactNativeWebView,
        isAndroid: isAndroid,
        isIOS: isIOS,
        platform: isAndroid ? 'android' : (isIOS ? 'ios' : 'web'),
    };
};

const AlertBox = ({ isOpen, onClose, message, btnTxt, callback }) => {
    if (!isOpen) return null;
    const handleClicBtn = () => {
        if (callback) callback();
        onClose();
    };
    return (
        <Modal isOpen={isOpen}>
            <div className="modal-box" data-type="a">
                <div className="modal-top-box">
                    <div className="title-box">
                        <p style={{ whiteSpace: 'pre-line' }}>{message}</p>
                    </div>
                </div>
                <div className="modal-bottom-box">
                    <div className="btn-layout">
                        <button className="btn" data-btn-type="fill-main" data-btn-size="md" onClick={handleClicBtn}>
                            <span>{btnTxt}</span>
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

const StepsMain = () => {
    const navigate = useNavigate();
    const [environment] = useState(() => detectEnvironment());
    const [isChecking, setIsChecking] = useState(true);

    const [isRequesting, setIsRequesting] = useState(false);
    const requestingRef = useRef(false);

    const [alertState, setAlertState] = useState({
        isOpen: false, message: '', btnTxt: '확인', callback: null
    });

    const androidStepCounter = useWebStepCounter();

    const getUserKey = (key) => {
        try {
            const loginUser = sessionStorage.getItem("loginUser");
            if (loginUser) return `${key}_${JSON.parse(loginUser).usrInfoId}`;
        } catch (e) { /* ignore */ }
        return key;
    };

    const getDeniedKey = () => getUserKey(`healthPermissionDenied_${environment.isIOS ? 'ios' : 'android'}`);
    const getGrantedKey = () => getUserKey('healthPermissionGranted');
    const getDeniedCountKey = () => getUserKey('hcPermissionDeniedCount');

    const showSettingsGuidePopup = () => {
        setAlertState({
            isOpen: true,
            message: environment.isIOS
                ? '건강 데이터 권한이 거부되었습니다.\n\n설정 > 개인정보 보호 > 건강에서\n앱 권한을 모두 허용해주세요.\n\n설정 후 다시 걸음수 메뉴에 들어오시면\n자동으로 연동됩니다.'
                : '건강 데이터 권한이 거부되었습니다.\n\nHealth Connect에서\n걸음 수 읽기 권한을 허용해주세요.\n\n설정 후 다시 걸음수 메뉴에 들어오시면\n자동으로 연동됩니다.',
            btnTxt: environment.isAndroid ? '설정으로 이동' : '확인',
            callback: environment.isAndroid ? () => {
                navigate(-1);
                setTimeout(() => {
                    if (window.ReactNativeWebView) {
                        window.ReactNativeWebView.postMessage(JSON.stringify({
                            type: 'OPEN_HEALTH_CONNECT_SETTINGS'
                        }));
                    }
                }, 300);
            } : () => { navigate(-1) }
        });
    };

    const showInstallHealthConnectPopup = () => {
        setAlertState({
            isOpen: true,
            message: '걸음수 측정을 위해\nHealth Connect 앱 설치가 필요합니다.',
            btnTxt: '설치하러 가기',
            callback: () => {
                if (window.ReactNativeWebView) {
                    window.ReactNativeWebView.postMessage(JSON.stringify({
                        type: 'OPEN_EXTERNAL_URL',
                        url: 'https://play.google.com/store/apps/details?id=com.google.android.apps.healthdata'
                    }));
                }
                navigate(-1);
            }
        });
    };

    const showUnsupportedDevicePopup = () => {
        setAlertState({
            isOpen: true,
            message: '이 기기에서는 걸음수 측정 기능을\n사용할 수 없습니다.\n\n(Android 9 이상, Health Connect 필요)',
            btnTxt: '확인',
            callback: () => navigate(-1)
        });
    };

    // ── useEffect #1: 초기 진입 시 권한 자동 체크 ──
    useEffect(() => {
        if (isRequesting) return;

        const checkInitialPermission = async () => {
            if (!environment.isReactNativeWebView) {
                setIsChecking(false);
                return;
            }

            if (environment.isIOS) {
                try {
                    const status = await healthAPI.checkPermission();
                    if (status.permissionGranted) {
                        navigate(URL.STEPS_DASHBOARD, { replace: true });
                    } else {
                        setIsChecking(false);
                    }
                } catch (e) {
                    setIsChecking(false);
                }
            } else if (environment.isAndroid) {
                if (androidStepCounter.permissionChecked) {
                    if (androidStepCounter.hasPermission) {
                        navigate(URL.STEPS_DASHBOARD, { replace: true });
                    } else {
                        setIsChecking(false);
                        // 초기 진입 시에는 팝업 안 띄움 — 버튼 누를 때 처리
                    }
                } else if (androidStepCounter.checkPermission) {
                    androidStepCounter.checkPermission();
                }
            }
        };
        checkInitialPermission();
    }, [
        environment,
        navigate,
        androidStepCounter?.permissionChecked,
        androidStepCounter?.hasPermission,
        isRequesting
    ]);

    // ── useEffect #2: 포그라운드 복귀 감지 (설정 앱 다녀왔을 때) ──
    useEffect(() => {
        const handleVisibilityChange = async () => {
            if (document.visibilityState !== 'visible' || !environment.isReactNativeWebView) return;
            if (isRequesting) return;

            const deniedKey = getDeniedKey();

            if (environment.isIOS) {
                if (localStorage.getItem(deniedKey) !== 'true') return;
                try {
                    const status = await healthAPI.checkPermission();
                    if (status.permissionGranted) {
                        localStorage.removeItem(deniedKey);
                        localStorage.setItem(getGrantedKey(), 'true');
                        navigate(URL.STEPS_DASHBOARD, { replace: true });
                    }
                } catch (e) { /* ignore */ }
            } else if (environment.isAndroid && androidStepCounter?.checkPermission) {
                localStorage.removeItem(deniedKey);
                androidStepCounter.checkPermission(true);
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [environment, navigate, androidStepCounter, isRequesting]);

    // ── useEffect #3: 안드로이드 권한 요청 응답 (버튼 클릭 후에만 반응) ──
    useEffect(() => {
        if (!environment.isAndroid || !environment.isReactNativeWebView) return;

        const handleNativeMessage = (event) => {
            try {
                const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
                if (data.type !== 'STEP_COUNTER_PERMISSION_STATUS') return;

                if (!requestingRef.current) return;

                if (data.status === 'HEALTH_CONNECT_UNAVAILABLE') {
                    setIsRequesting(false);
                    requestingRef.current = false;
                    showInstallHealthConnectPopup();
                } else if (data.status === 'PERMISSION_DENIED') {
                    const deniedCountKey = getDeniedCountKey();
                    const prev = parseInt(localStorage.getItem(deniedCountKey) || '0', 10);
                    localStorage.setItem(deniedCountKey, String(prev + 1));
                    localStorage.setItem(getDeniedKey(), 'true');

                    setIsRequesting(false);
                    requestingRef.current = false;
                    showSettingsGuidePopup();
                } else if (data.status === 'PERMISSION_GRANTED') {
                    localStorage.setItem(getGrantedKey(), 'true');
                    localStorage.removeItem(getDeniedKey());
                    localStorage.removeItem(getDeniedCountKey());
                    setAlertState({
                        isOpen: true,
                        message: '건강 데이터 연동이 완료되었습니다.',
                        btnTxt: '확인',
                        callback: () => {
                            setIsRequesting(false);
                            requestingRef.current = false;
                            navigate(URL.STEPS_DASHBOARD, { replace: true });
                        }
                    });
                }
            } catch (e) { /* ignore */ }
        };

        window.addEventListener('message', handleNativeMessage);
        return () => window.removeEventListener('message', handleNativeMessage);
    }, [environment, navigate]);

    // ── 연동하기 버튼 클릭 핸들러 ──
    const handleConnect = async () => {
        if (requestingRef.current) return;

        const deniedKey = getDeniedKey();
        const deniedCountKey = getDeniedCountKey();

        // 과거 거절 이력 있어도 이미 권한 허용됐으면 바로 이동
        if (localStorage.getItem(deniedKey) === 'true') {
            if (environment.isAndroid && androidStepCounter?.hasPermission) {
                localStorage.removeItem(deniedKey);
                localStorage.removeItem(deniedCountKey);
                localStorage.setItem(getGrantedKey(), 'true');
                navigate(URL.STEPS_DASHBOARD, { replace: true });
                return;
            }
            showSettingsGuidePopup();
            return;
        }

        requestingRef.current = true;
        setIsRequesting(true);

        if (environment.isIOS) {
            try {
                const curStatus = await healthAPI.checkPermission();
                if (curStatus.permissionGranted) {
                    setIsRequesting(false);
                    requestingRef.current = false;
                    navigate(URL.STEPS_DASHBOARD, { replace: true });
                    return;
                }

                const result = await healthAPI.requestPermission();
                if (result && result.granted) {
                    localStorage.setItem(getGrantedKey(), 'true');
                    localStorage.removeItem(deniedKey);

                    setAlertState({
                        isOpen: true,
                        message: '건강 데이터 연동이 완료되었습니다.',
                        btnTxt: '확인',
                        callback: () => {
                            setIsRequesting(false);
                            requestingRef.current = false;
                            navigate(URL.STEPS_DASHBOARD, { replace: true });
                        }
                    });
                } else {
                    localStorage.setItem(deniedKey, 'true');
                    setIsRequesting(false);
                    requestingRef.current = false;
                    showSettingsGuidePopup();
                }
            } catch (error) {
                localStorage.setItem(deniedKey, 'true');
                setIsRequesting(false);
                requestingRef.current = false;
                showSettingsGuidePopup();
            }
        } else if (environment.isAndroid) {
            const deniedCount = parseInt(localStorage.getItem(deniedCountKey) || '0', 10);
            if (deniedCount >= 1) {
                setIsRequesting(false);
                requestingRef.current = false;
                showSettingsGuidePopup();
                return;
            }

            if (window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(JSON.stringify({
                    type: 'STEP_REQUEST_PERMISSION'
                }));
            } else {
                setIsRequesting(false);
                requestingRef.current = false;
            }
        } else {
            setIsRequesting(false);
            requestingRef.current = false;
            navigate(URL.STEPS_DASHBOARD, { replace: true });
        }
    };

    if (isChecking) return null;

    return (
        <>
            <HeadSub title="걸음수 기록" />
            <div id="sub-content" data-layout="white">
                <div className="inner-wrap">
                    <div className={style['steps-main-box']}>
                        <div className={style['img-box']}>
                            <object data={imgSteps} aria-hidden='true' />
                        </div>
                        <p>내 건강 데이터를 연동하면 <br />내 걸음수가 자동으로 기록됩니다.</p>
                    </div>
                    <div className="bottom-fixed-box">
                        <div className="inner-box">
                            <div className='btn-layout' data-btn-num="1">
                                <button
                                    className="btn"
                                    data-btn-type="fill-main"
                                    data-btn-size="lg"
                                    onClick={handleConnect}
                                    disabled={isRequesting}
                                >
                                    <span>내 건강 데이터 연동하기</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <AlertBox
                isOpen={alertState.isOpen}
                onClose={() => setAlertState({ ...alertState, isOpen: false })}
                message={alertState.message}
                btnTxt={alertState.btnTxt}
                callback={alertState.callback}
            />
        </>
    );
};

export default StepsMain;